import { keccak256, type Account, type Address, type Hex, type WalletClient } from 'viem';

import { config, deployment } from '../config.ts';
import { cancelTypedData, encodeCall, typedData, type Intent } from '../../../offchain/src/eip712.ts';
import { relayer, type IntentView } from './relayer.ts';

/** A session key signs without a wallet prompt; the wallet is the fallback and the only signer for keys. */
export type Signer = { kind: 'session'; account: Account } | { kind: 'wallet'; client: WalletClient };

/** Seconds an intent stays valid: short, so the relayer has little room to pick its execution time. */
export const INTENT_TTL_SEC = 45;

import { newNonce } from './nonce.ts';

export { newNonce };

export const deadlineIn = (sec = INTENT_TTL_SEC) => BigInt(Math.floor(Date.now() / 1000) + sec);

export async function sign(intent: Intent, signer: Signer): Promise<Hex> {
  return signTyped(typedData(intent, deployment, config.chainId), signer);
}

export async function signTyped(td: Record<string, unknown>, signer: Signer): Promise<Hex> {
  if (signer.kind === 'session') {
    if (!signer.account.signTypedData) throw new Error('session key cannot sign typed data');
    return signer.account.signTypedData(td as never);
  }
  return signer.client.signTypedData({ account: signer.client.account!, ...td } as never);
}

/** Signs and hands the intent to the relayer; resolves with its id once accepted. `limit` rests an open. */
export async function submit(intent: Intent, signer: Signer, type?: 'limit'): Promise<Hex> {
  if (intent.action === 'registerSessionKey' && signer.kind !== 'wallet') {
    throw new Error('only the wallet can register a session key');
  }
  const signature = await sign(intent, signer);
  // the id is the keccak of the calldata, the same the relayer computes
  const id = keccak256(encodeCall(intent, signature, deployment).data);
  try {
    return (await relayer.submit(intent, signature, type)).id;
  } catch (e) {
    // an answer from the relayer (a 4xx) is final; a lost connection is not: the POST may have landed and only
    // its answer got lost, in which case the intent is there under its id
    if (!(e instanceof TypeError)) throw e;
    for (let i = 0; i < 5; i++) {
      await new Promise((r) => setTimeout(r, 500));
      try {
        await relayer.intent(id);
        return id;
      } catch {
        // still unreachable, or really never received
      }
    }
    throw e;
  }
}

/**
 * Rests a take profit: a close of `positionId` signed with `px` as its worst exit, which the relayer
 * sends once the price reaches it. It lasts 30 days, or until the session key that signs it expires.
 */
export async function placeTakeProfit(user: Address, positionId: bigint, px: bigint, signer: Signer, sessionExpiry?: number): Promise<Hex> {
  const now = Math.floor(Date.now() / 1000);
  const end = now + LIMIT_TTLS[LIMIT_TTLS.length - 1]!.sec;
  const deadline = signer.kind === 'session' && sessionExpiry !== undefined ? Math.min(end, sessionExpiry - 60) : end;
  return submit({ action: 'close', request: { user, positionIds: [positionId], limitPx: [px], nonce: newNonce(), deadline: BigInt(deadline) } }, signer, 'limit');
}

/** How long a limit order may rest; the relayer caps it at 30 days, and at the session key's expiry */
export const LIMIT_TTLS = [
  { label: '1h', sec: 3_600 },
  { label: '1d', sec: 86_400 },
  { label: '7d', sec: 7 * 86_400 },
  { label: '30d', sec: 30 * 86_400 },
] as const;

/**
 * Takes a queued intent back. Only the relayer queue forgets it: once a batcher holds it, it runs (409).
 * The session key signs silently when there is one, like for orders.
 */
export async function cancelIntent(id: Hex, signer: Signer): Promise<void> {
  await relayer.cancel(id, await signTyped(cancelTypedData(id, deployment, config.chainId), signer));
}

/** run() result for an intent its user took back from the queue */
export class CancelledError extends Error {
  constructor() {
    super('cancelled');
  }
}

const FINAL = new Set(['succeeded', 'failed', 'expired', 'cancelled']);

/** The relayer stopped answering before the intent reached a final state: it may still run */
export class UnsettledError extends Error {
  readonly id: Hex;

  constructor(id: Hex) {
    super('The relayer stopped answering before the result came: check the Status tab before placing it again.');
    this.id = id;
  }
}

/**
 * Polls the relayer until the intent is executed, failed or expired; `onUpdate` sees each state on the way.
 * A lost packet or a relayer restart on the way is not a failure of the order, which may run all the same:
 * polling goes on until the timeout, and only then gives up with an UnsettledError.
 */
export async function settle(id: Hex, onUpdate?: (v: IntentView) => void, timeoutMs = (INTENT_TTL_SEC + 15) * 1000): Promise<IntentView> {
  const end = Date.now() + timeoutMs;
  let last: IntentView | undefined;
  for (;;) {
    try {
      const v = await relayer.intent(id);
      last = v;
      onUpdate?.(v);
      if (FINAL.has(v.status)) return v;
    } catch {
      // keep asking
    }
    if (Date.now() > end) {
      if (last) return { ...last, status: 'expired', error: last.error ?? 'no result from the relayer' };
      throw new UnsettledError(id);
    }
    await new Promise((r) => setTimeout(r, 400));
  }
}

/** Submit, wait, and throw on anything but success: what most buttons want. */
export async function run(intent: Intent, signer: Signer, onUpdate?: (v: IntentView) => void): Promise<IntentView> {
  const v = await settle(await submit(intent, signer), onUpdate);
  if (v.status === 'cancelled') throw new CancelledError();
  if (v.status !== 'succeeded') throw new Error(v.error ?? `intent ${v.status}`);
  return v;
}
